import { useEffect, useState, useCallback } from 'react';
import { Search, Eye, Trash2, Upload, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { listDocuments, createDocument, deleteDocument, getDocument, getDocumentStats } from '../../../entities/document';
import { uploadFileToServer } from '../../../shared/api';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const thresh = 1024;
  if (Math.abs(bytes) < thresh) return bytes + ' B';
  const units = ['KB', 'MB', 'GB', 'TB'];
  let u = -1;
  do {
    bytes /= thresh;
    ++u;
  } while (Math.abs(bytes) >= thresh && u < units.length - 1);
  return bytes.toFixed(1) + ' ' + units[u];
};

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export function DocumentsPage() {
  // UI state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All'); // PDF/JPEG/PNG/All
  const [dateFilter, setDateFilter] = useState('30'); // '7', '30', 'all'
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState('General');
  const [statsData, setStatsData] = useState(null);

  // fetch documents
  const fetchDocs = useCallback(async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams();
      params.set('page', page);
      params.set('limit', limit);

      if (search && search.trim()) params.set('q', search.trim());
      if (typeFilter && typeFilter !== 'All') params.set('file_type', typeFilter.toUpperCase());
      if (selectedStatus && selectedStatus !== 'All') params.set('document_type', selectedStatus);
      // dateFilter -> translate to uploadedAfter (ISO)
      if (dateFilter && dateFilter !== 'all') {
        const days = parseInt(dateFilter, 10);
        const after = new Date();
        after.setDate(after.getDate() - days);
        params.set('uploadedAfter', after.toISOString());
      }

      const query = `?${params.toString()}`;
      const res = await listDocuments(query);
      setItems(res.data || []);
      setTotal(res.total || 0);
      const res1 = await getDocumentStats();
      setStatsData(res1);
    } catch (err) {
      console.error('Fetch docs error', err);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [page, limit, search, typeFilter, dateFilter, selectedStatus]);

  useEffect(() => {
    fetchDocs();
  }, [fetchDocs]);

  // handle search change (resets to page 1)
  const onSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  const onTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setPage(1);
  };

  const onDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    setPage(1);
  };

  const onStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setPage(1);
  };

  // File selected in modal
  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0]);
    }
  };

  // Upload flow:
  // 1) POST file to /files/upload -> returns url/key
  // 2) createDocument metadata record with filename, file_type, upload_date, size (bytes), document_type (status)
  const handleUploadSubmit = async (ev) => {
    ev.preventDefault();
    if (!selectedFile) {
      toast.error('Select a file first');
      return;
    }
    try {
      setUploading(true);
      // upload file to server (S3)
      const uploadRes = await uploadFileToServer(selectedFile);
      const fileUrl = uploadRes?.url;
      // create metadata record
      const payload = {
        filename: selectedFile.name,
        file_type: selectedFile.name.split('.').pop().toUpperCase(),
        upload_date: new Date().toISOString(),
        size: selectedFile.size, // bytes
        document_type: selectedStatus || 'General',
        // optionally include file_url if your schema supports it
        file_url: fileUrl,
        object_key: uploadRes.key,
      };
      await createDocument(payload);

      // reset
      setSelectedFile(null);
      setSelectedStatus('General');
      setIsModalOpen(false);
      setPage(1); // show newest results
      fetchDocs();
      toast.success('File uploaded and document record created');
    } catch (err) {
      console.error('Upload error', err);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleView = async (doc) => {
    try {
      const res = await getDocument(doc.id);
      if (res.presigned_url) {
        window.open(res.presigned_url, '_blank');
      } else {
        toast.error('No file url available for this document');
      }
    } catch (err) {
      console.error('Get document error', err);
      toast.error('Failed to fetch document url');
    }
  };

  const handleDelete = async (doc) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Delete ${doc.filename || doc.name || 'this document'}? This action cannot be undone.`)) return;
    try {
      await deleteDocument(doc.id || doc.id);
      toast.success('Document deleted');
      // re-fetch page; if last item on page was deleted and page > 1, try to move back one page
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
      else fetchDocs();
    } catch (err) {
      console.error('Delete error', err);
      toast.error('Failed to delete document');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="p-6 w-full space-y-6">
      {/* Stat cards (you can swap counts in with server results later) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-xl font-semibold">{total}</p>
          <p className="text-sm text-gray-500">Total Documents</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-xl font-semibold text-blue-600">{statsData?.totalLoad}</p>
          <p className="text-sm text-gray-500">Load Documents</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-xl font-semibold text-green-600">{statsData?.totalGeneral}</p>
          <p className="text-sm text-gray-500">General Documents</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 text-center">
          <p className="text-xl font-semibold">{statsData?.totalSize}</p>
          <p className="text-sm text-gray-500">Storage Used</p>
        </div>
      </div>

      {/* Search + Filters + Upload CTA */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
            <input type="text" placeholder="Search documents..." value={search} onChange={onSearchChange} className="pl-9 pr-3 py-2 rounded-lg border border-gray-300 bg-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full" />
          </div>

          <select value={typeFilter} onChange={onTypeFilterChange} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
            <option value="All">All Types</option>
            <option value="PDF">PDF</option>
            <option value="PNG">PNG</option>
            <option value="JPEG">JPEG</option>
            <option value="JPG">JPG</option>
          </select>

          <select value={dateFilter} onChange={onDateFilterChange} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="all">All time</option>
          </select>

          <select value={selectedStatus} onChange={onStatusChange} className="px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
            <option value="All">All Status</option>
            <option value="General">General</option>
            {/* You can populate dynamic status options from API */}
            <option value="TF-2025-000123">TF-2025-000123</option>
          </select>
        </div>

        {/* Upload CTA aligned right */}
        <button onClick={() => setIsModalOpen(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2">
          <Upload className="w-4 h-4" />
          Upload Document
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead className="bg-gray-50 text-gray-600 font-medium">
            <tr>
              <th className="px-6 py-3">FILE NAME</th>
              <th className="px-6 py-3">STATUS</th>
              <th className="px-6 py-3">TYPE</th>
              <th className="px-6 py-3">SIZE</th>
              <th className="px-6 py-3">UPLOAD DATE</th>
              <th className="px-6 py-3">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="6" className="px-6 py-6 text-center">
                  Loading...
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-6 py-6 text-center text-gray-500">
                  No documents found
                </td>
              </tr>
            ) : (
              items.map((doc, idx) => (
                <tr key={doc.id || idx} className="border-t">
                  <td className="px-6 py-3 text-gray-800 flex items-center gap-2">
                    <span className="text-blue-500">📄</span>
                    <div>
                      <div className="font-medium">{doc.filename || doc.name}</div>
                      <div className="text-xs text-gray-500">{doc.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-3">{doc.document_type === 'General' ? <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">General</span> : <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">{doc.document_type}</span>}</td>
                  <td className="px-6 py-3">{doc.file_type}</td>
                  <td className="px-6 py-3">{doc.size ? formatBytes(doc.size) : '-'}</td>
                  <td className="px-6 py-3">{doc.upload_date ? formatDate(doc.upload_date) : formatDate(doc.created_at)}</td>
                  <td className="px-6 py-3 flex gap-3">
                    <Eye className="w-4 h-4 text-gray-500 cursor-pointer hover:text-blue-600" onClick={() => handleView(doc)} />

                    <Trash2 className="w-4 h-4 text-red-500 cursor-pointer hover:text-red-700" onClick={() => handleDelete(doc)} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-3 bg-gray-50 border-t">
          <span className="text-sm text-gray-500">
            Showing <span className="font-medium">{(page - 1) * limit + 1}</span> - <span className="font-medium">{Math.min(page * limit, total)}</span> of {total}
          </span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">
              &lt;
            </button>
            <button className="px-3 py-1 text-sm rounded bg-blue-600 text-white">{page}</button>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="px-2 py-1 text-sm rounded hover:bg-gray-200 disabled:opacity-50">
              &gt;
            </button>
          </div>
        </div>
      </div>

      {/* Upload Document Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
            {/* Header */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Upload Document</h2>
              <button onClick={() => setIsModalOpen(false)}>
                <X className="w-5 h-5 text-gray-500 hover:text-gray-700" />
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleUploadSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select File</label>
                <input type="file" onChange={handleFileChange} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm" />
                {selectedFile && (
                  <div className="mt-2 text-sm text-gray-600">
                    Selected: <span className="font-medium">{selectedFile.name}</span> — {formatBytes(selectedFile.size)}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select name="status" value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm">
                  <option value="General">General</option>
                  <option value="TF-2025-000123">TF-2025-000123</option>
                  <option value="TF-2025-000124">TF-2025-000124</option>
                  <option value="TF-2025-000125">TF-2025-000125</option>
                </select>
              </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100">
                  Cancel
                </button>
                <button type="submit" disabled={uploading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                  {uploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
